import clsx from 'clsx'
import { Pause, Play, Volume2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import { ProtoDropdown } from '@/features/studio/components/ui-kit'

const BAR_COUNT = 16

const PLAYBACK_RATES = [
  { value: '0.5', label: '0.5×' },
  { value: '0.75', label: '0.75×' },
  { value: '1', label: '1×' },
  { value: '1.25', label: '1.25×' },
  { value: '1.5', label: '1.5×' },
  { value: '2', label: '2×' },
]

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00'
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function barHeights(seed: string): number[] {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return Array.from({ length: BAR_COUNT }, (_, i) => {
    const t = Math.sin((h + i) * 1.37) * 0.5 + 0.5
    return 0.22 + t * 0.68
  })
}

export type AudioWavePlayerVariant = 'card' | 'hero'

export function AudioWavePlayer({
  src,
  title,
  variant = 'card',
  className,
  interactiveClassName,
  onError,
  onExpand,
}: {
  src: string
  title?: string
  variant?: AudioWavePlayerVariant
  className?: string
  /** 画布节点内阻止拖拽，如 nodrag nopan nowheel */
  interactiveClassName?: string
  onError?: () => void
  onExpand?: () => void
}) {
  const { t } = useTranslation()
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const [current, setCurrent] = useState(0)
  const [duration, setDuration] = useState(0)
  const [failed, setFailed] = useState(false)
  const [ready, setReady] = useState(false)
  const [playbackRate, setPlaybackRate] = useState('1')

  const isHero = variant === 'hero'
  const bars = useMemo(() => barHeights(src || title || 'audio'), [src, title])
  const ic = interactiveClassName ?? ''

  const toggle = useCallback(() => {
    const el = audioRef.current
    if (!el || failed) return
    if (el.paused) {
      void el.play().catch(() => setFailed(true))
    } else {
      el.pause()
    }
  }, [failed])

  useEffect(() => {
    setPlaying(false)
    setCurrent(0)
    setDuration(0)
    setFailed(false)
    setReady(false)
    setPlaybackRate('1')
  }, [src])

  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    el.playbackRate = Number(playbackRate) || 1
  }, [playbackRate, src])

  useEffect(() => {
    const el = audioRef.current
    if (!el) return

    const onPlay = () => setPlaying(true)
    const onPause = () => setPlaying(false)
    const onTime = () => setCurrent(el.currentTime)
    const onMeta = () => {
      setDuration(el.duration)
      setReady(Number.isFinite(el.duration) && el.duration > 0)
    }
    const onEnded = () => {
      setPlaying(false)
      setCurrent(0)
    }
    const onErr = () => {
      setFailed(true)
      setPlaying(false)
      onError?.()
    }

    el.addEventListener('play', onPlay)
    el.addEventListener('pause', onPause)
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('loadedmetadata', onMeta)
    el.addEventListener('durationchange', onMeta)
    el.addEventListener('ended', onEnded)
    el.addEventListener('error', onErr)

    return () => {
      el.pause()
      el.removeEventListener('play', onPlay)
      el.removeEventListener('pause', onPause)
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('loadedmetadata', onMeta)
      el.removeEventListener('durationchange', onMeta)
      el.removeEventListener('ended', onEnded)
      el.removeEventListener('error', onErr)
    }
  }, [src, onError])

  const progress = duration > 0 ? Math.min(1, current / duration) : 0
  const showTitle = !!title && !isHero

  return (
    <div
      className={clsx(
        'cn-audio-wave relative overflow-hidden bg-[#0a0a12]',
        isHero ? 'cn-audio-wave--hero h-full min-h-[108px] w-full' : 'aspect-square w-full',
        className,
      )}
      role="group"
      aria-label={title || t('assets.tab.audio')}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_70%_at_50%_100%,rgba(244,114,182,0.18),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_20%_0%,rgba(99,102,241,0.12),transparent_50%)]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-pink-400/30 to-transparent" />

      <div
        className={clsx(
          'absolute inset-0 flex items-end justify-center',
          isHero ? 'gap-[2px] px-4 pb-11 pt-3' : 'gap-[3px] px-5 pb-[4.25rem] pt-6',
        )}
      >
        {bars.map((h, i) => (
          <span
            key={i}
            className={clsx(
              'asset-audio-bar rounded-full',
              isHero ? 'w-[2px] max-h-[58%] min-h-[6%]' : 'w-[3px] max-h-[72%] min-h-[8%]',
              failed
                ? 'bg-zinc-600/40'
                : playing
                  ? 'asset-audio-bar--live bg-gradient-to-t from-indigo-500/50 via-fuchsia-400/85 to-pink-300/95'
                  : 'bg-gradient-to-t from-indigo-500/25 to-pink-400/45',
            )}
            style={{
              height: `${Math.round(h * 100)}%`,
              animationDelay: playing ? `${i * 0.055}s` : undefined,
              opacity: playing ? undefined : 0.55 + (i % 3) * 0.08,
            }}
          />
        ))}
      </div>

      <div
        className={clsx(
          'absolute inset-x-0 bottom-0 flex flex-col bg-gradient-to-t from-[#0a0a12] via-[#0a0a12]/95 to-transparent',
          isHero ? 'gap-1.5 px-2.5 pb-2 pt-5' : 'gap-2 px-3 pb-3 pt-8',
        )}
      >
        <div className="h-0.5 overflow-hidden rounded-full bg-white/[0.06]">
          <div
            className={clsx(
              'h-full rounded-full transition-[width] duration-150',
              failed ? 'bg-rose-500/50' : 'bg-gradient-to-r from-indigo-400 to-pink-400',
            )}
            style={{ width: `${progress * 100}%` }}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            disabled={failed}
            onClick={(e) => {
              e.stopPropagation()
              toggle()
            }}
            className={clsx(
              ic,
              'rounded-full',
              isHero ? 'size-8' : 'size-9',
              failed
                ? 'border-white/10 bg-white/[0.04] text-zinc-600'
                : 'border-pink-400/25 bg-gradient-to-br from-indigo-500/30 to-pink-500/25 text-pink-100 shadow-[0_0_20px_rgba(244,114,182,0.2)] hover:border-pink-400/45 hover:text-white',
            )}
            aria-label={playing ? t('assets.audioPause') : t('assets.audioPlay')}
          >
            {playing ? (
              <Pause className={clsx(isHero ? 'h-3.5 w-3.5' : 'h-4 w-4')} fill="currentColor" />
            ) : (
              <Play className={clsx('ml-0.5', isHero ? 'h-3.5 w-3.5' : 'h-4 w-4')} fill="currentColor" />
            )}
          </Button>

          <div className="min-w-0 flex-1">
            {showTitle ? <p className="truncate text-[10px] font-medium text-zinc-300">{title}</p> : null}
            <p className={clsx('tabular-nums text-zinc-500', isHero ? 'text-[11px]' : 'text-[10px]')}>
              {failed
                ? t('assets.audioLoadFailed')
                : `${formatTime(current)} / ${ready ? formatTime(duration) : '--:--'}`}
            </p>
          </div>

          {!failed ? (
            <ProtoDropdown
              compact
              portal
              className={clsx(ic, 'cn-audio-wave__rate w-[4.5rem] shrink-0')}
              value={playbackRate}
              onValueChange={setPlaybackRate}
              options={PLAYBACK_RATES}
              aria-label={t('assets.audioPlaybackRate')}
            />
          ) : null}

          {onExpand && !failed ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={(e) => {
                e.stopPropagation()
                audioRef.current?.pause()
                onExpand()
              }}
              title={t('assets.mediaPreview')}
              aria-label={t('assets.mediaPreview')}
            >
              <Volume2 className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      <audio ref={audioRef} src={src} preload="metadata" className="hidden" />
    </div>
  )
}
